import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const useScreenCaptureDetection = (isActive = true, fileName = null) => {
  const { user } = useAuth();
  const detectionRef = useRef({
    keySequences: [],
    focusChanges: [],
    devToolsWarnings: 0,
    lastActivity: Date.now()
  });

  // Report suspicious activity to backend
  const reportSuspiciousActivity = useCallback(async (activityType, details = {}) => {
    if (!user || !isActive) return;

    try {
      await fetch('/api/security/suspicious-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          activityType,
          fileName,
          details: {
            ...details,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            url: window.location.href
          }
        })
      });
    } catch (error) {
      console.error('Failed to report suspicious activity:', error);
    }
  }, [user, isActive, fileName]);

  // Show warning message
  const showWarning = useCallback((message) => {
    // Create warning overlay
    const warningDiv = document.createElement('div');
    warningDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(220, 38, 38, 0.95);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      font-weight: bold;
      z-index: 10000;
      text-align: center;
      padding: 20px;
    `;
    warningDiv.innerHTML = `
      <div>
        <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
        <div>${message}</div>
        <div style="font-size: 16px; margin-top: 20px; opacity: 0.8;">
          Ez a tevékenység naplózásra került.
        </div>
      </div>
    `;

    document.body.appendChild(warningDiv);

    // Remove warning after 3 seconds
    setTimeout(() => {
      if (document.body.contains(warningDiv)) {
        document.body.removeChild(warningDiv);
      }
    }, 3000);
  }, []);

  // Keyboard event handler
  const handleKeyDown = useCallback((event) => {
    if (!isActive) return;

    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    const shift = event.shiftKey;
    const alt = event.altKey;
    const meta = event.metaKey;

    // Print Screen detection
    if (key === 'printscreen') {
      event.preventDefault();
      reportSuspiciousActivity('print_screen_key', {
        keyPressed: 'PrintScreen',
        modifiers: { ctrl, shift, alt, meta }
      });
      showWarning('Képernyőkép készítés nem engedélyezett!');
      return;
    }

    // Snipping Tool (Windows)
    if (ctrl && shift && key === 's') {
      event.preventDefault();
      reportSuspiciousActivity('screenshot_attempt', {
        keyPressed: 'Ctrl+Shift+S',
        tool: 'Snipping Tool'
      });
      showWarning('Képernyőkép készítés nem engedélyezett!');
      return;
    }

    // macOS screenshot shortcuts
    if (meta && shift && (key === '3' || key === '4' || key === '5')) {
      event.preventDefault();
      reportSuspiciousActivity('screenshot_attempt', {
        keyPressed: `Cmd+Shift+${key}`,
        platform: 'macOS'
      });
      showWarning('Képernyőkép készítés nem engedélyezett!');
      return;
    }

    // Developer Tools detection
    if ((key === 'f12') || 
        (ctrl && shift && key === 'i') || 
        (ctrl && shift && key === 'j') ||
        (ctrl && shift && key === 'c')) {
      event.preventDefault();
      reportSuspiciousActivity('dev_tools_attempt', {
        keyPressed: key === 'f12' ? 'F12' : 'Ctrl+Shift+' + key.toUpperCase(),
        action: 'Developer Tools'
      });
      showWarning('Fejlesztői eszközök használata nem engedélyezett!');
      return;
    }

    // Print attempt
    if (ctrl && key === 'p') {
      event.preventDefault();
      reportSuspiciousActivity('print_attempt', {
        keyPressed: 'Ctrl+P'
      });
      showWarning('Nyomtatás nem engedélyezett!');
      return;
    }

    // Copy attempt
    if (ctrl && key === 'c') {
      reportSuspiciousActivity('copy_attempt', {
        keyPressed: 'Ctrl+C'
      });
      // Don't prevent this as it might be legitimate
    }

    // Select all attempt
    if (ctrl && key === 'a') {
      reportSuspiciousActivity('select_all_attempt', {
        keyPressed: 'Ctrl+A'
      });
    }

    // Log suspicious key combinations
    const suspiciousKeys = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10', 'f11'];
    if (suspiciousKeys.includes(key)) {
      reportSuspiciousActivity('function_key_pressed', {
        keyPressed: key.toUpperCase(),
        modifiers: { ctrl, shift, alt, meta }
      });
    }
  }, [isActive, reportSuspiciousActivity, showWarning]);

  // Context menu handler
  const handleContextMenu = useCallback((event) => {
    if (!isActive) return;

    event.preventDefault();
    reportSuspiciousActivity('context_menu_attempt', {
      elementType: event.target.tagName,
      elementClass: event.target.className
    });
    showWarning('Jobb klikk nem engedélyezett!');
  }, [isActive, reportSuspiciousActivity, showWarning]);

  // Visibility change handler
  const handleVisibilityChange = useCallback(() => {
    if (!isActive) return;

    if (document.hidden) {
      reportSuspiciousActivity('page_visibility_hidden', {
        reason: 'Tab switch or window minimized'
      });
    }
  }, [isActive, reportSuspiciousActivity]);

  // Focus/blur handlers
  const handleWindowBlur = useCallback(() => {
    if (!isActive) return;

    detectionRef.current.focusChanges.push({
      type: 'blur',
      timestamp: Date.now()
    });

    // Keep only last 10 focus changes
    if (detectionRef.current.focusChanges.length > 10) {
      detectionRef.current.focusChanges = detectionRef.current.focusChanges.slice(-10);
    }

    // Check for suspicious patterns (frequent focus changes)
    const recentChanges = detectionRef.current.focusChanges.filter(
      change => Date.now() - change.timestamp < 60000 // Last minute
    );

    if (recentChanges.length > 5) {
      reportSuspiciousActivity('focus_loss_pattern', {
        changesInLastMinute: recentChanges.length,
        pattern: 'frequent_focus_changes'
      });
    }
  }, [isActive, reportSuspiciousActivity]);

  const handleWindowFocus = useCallback(() => {
    if (!isActive) return;

    detectionRef.current.focusChanges.push({
      type: 'focus',
      timestamp: Date.now()
    });
  }, [isActive]);

  // DevTools detection (console commands)
  const setupDevToolsDetection = useCallback(() => {
    if (!isActive) return;

    // Override console methods to detect DevTools usage
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = function(...args) {
      detectionRef.current.devToolsWarnings++;
      if (detectionRef.current.devToolsWarnings > 3) {
        reportSuspiciousActivity('dev_tools_opened', {
          method: 'console.log_override',
          warnings: detectionRef.current.devToolsWarnings
        });
      }
      return originalLog.apply(console, args);
    };

    console.error = function(...args) {
      detectionRef.current.devToolsWarnings++;
      return originalError.apply(console, args);
    };

    console.warn = function(...args) {
      detectionRef.current.devToolsWarnings++;
      return originalWarn.apply(console, args);
    };

    // DevTools detection through timing
    const devToolsCheck = () => {
      const startTime = performance.now();
      console.clear();
      const endTime = performance.now();
      
      if (endTime - startTime > 100) {
        reportSuspiciousActivity('dev_tools_opened', {
          method: 'timing_detection',
          timing: endTime - startTime
        });
      }
    };

    // Check every 30 seconds
    const devToolsInterval = setInterval(devToolsCheck, 30000);

    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      clearInterval(devToolsInterval);
    };
  }, [isActive, reportSuspiciousActivity]);

  // Mouse movement tracking for suspicious patterns
  const handleMouseMove = useCallback((event) => {
    if (!isActive) return;

    detectionRef.current.lastActivity = Date.now();

    // Detect rapid mouse movements (could indicate automation)
    const now = Date.now();
    if (!detectionRef.current.lastMouseMove) {
      detectionRef.current.lastMouseMove = now;
      detectionRef.current.mouseMovements = [];
    }

    const timeDiff = now - detectionRef.current.lastMouseMove;
    if (timeDiff < 10) { // Very fast movements
      detectionRef.current.mouseMovements.push(timeDiff);
      
      if (detectionRef.current.mouseMovements.length > 20) {
        detectionRef.current.mouseMovements = detectionRef.current.mouseMovements.slice(-20);
        
        const avgTime = detectionRef.current.mouseMovements.reduce((a, b) => a + b, 0) / 20;
        if (avgTime < 5) {
          reportSuspiciousActivity('suspicious_mouse_pattern', {
            averageTime: avgTime,
            pattern: 'automated_movement'
          });
        }
      }
    }

    detectionRef.current.lastMouseMove = now;
  }, [isActive, reportSuspiciousActivity]);

  // Setup all event listeners
  useEffect(() => {
    if (!isActive) return;

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('mousemove', handleMouseMove);

    const devToolsCleanup = setupDevToolsDetection();

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      reportSuspiciousActivity('drag_attempt', {
        elementType: e.target.tagName
      });
    };

    document.addEventListener('dragstart', handleDragStart);

    // Disable selection in PDF area
    const handleSelectStart = (e) => {
      if (e.target.closest('.pdf-container')) {
        e.preventDefault();
        reportSuspiciousActivity('text_selection_attempt', {
          elementType: e.target.tagName
        });
      }
    };

    document.addEventListener('selectstart', handleSelectStart);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('selectstart', handleSelectStart);
      
      if (devToolsCleanup) {
        devToolsCleanup();
      }
    };
  }, [
    isActive,
    handleKeyDown,
    handleContextMenu,
    handleVisibilityChange,
    handleWindowBlur,
    handleWindowFocus,
    handleMouseMove,
    setupDevToolsDetection,
    reportSuspiciousActivity
  ]);

  return {
    reportSuspiciousActivity,
    showWarning
  };
};

export default useScreenCaptureDetection; 